// On-site (live) event engraving. Facts come from the owner's 2026 event quotes
// and the event app spec (laser-engraving-event-app-tag-design, docs/SPEC.md).
// Describe the setup and process, never past live-event results, attendance
// numbers, or client names. See .seo/briefs/on-site-laser-engraving-nyc.md.
// Prices and capacity: the owner's event pricing (2026-11-03) in
// src/data/event-engraving.js, shared with the beauty and hat bar pages.
const { images } = require('./engraving-services')
const {
  eventPricing,
  eventCapacity,
  eventPriceAnswer,
  eventInsurance,
} = require('./event-engraving')

const facts = {
  path: '/services/on-site-laser-engraving-nyc/',
  reviewedDate: '2026-11-03',
  machine: 'xTool F2',
  bed: '115 × 115 mm',
  engravingArea: 'about 4.5 inches',
  piecesPerHour: '40–50',
  setupTime: 'about 30 minutes',
  bookingLead: 'four or more weeks',
  deposit: '50%',
}

// Owner 2026-11-03: publish an idea of investment, not a starting price.
const investment = {
  display: eventPricing.investment,
  basis: eventPricing.investmentNote,
}

const page = {
  path: facts.path,
  title: 'On-Site & Live Laser Engraving in NYC | PrintAWorld',
  description:
    'Live laser engraving stations for NYC events, trade shows, and brand activations. Guests personalize tags or leather from their phones.',
  eyebrow: 'On-site laser engraving / New York City',
  heading: 'On-site laser engraving for NYC events and brand activations.',
  answer:
    'PrintAWorld brings a live laser engraving station to your New York event. Guests scan a QR code at the booth, type a name, choose an approved font or icon, and follow their claim number on their phone while we engrave the piece on site. Each station is a portable, enclosed laser run by an engraver and an assistant, set up in about 30 minutes at a table-sized spot with a standard outlet.',
  cta: 'Get an event engraving quote',
  hero: 'leather',
}

const keyFacts = [
  {
    label: 'Per station',
    value: '40–50 / hour',
    note: 'About 250 in 3 hours with batch loading',
  },
  {
    label: 'Engraving area',
    value: 'Up to ~4.5″',
    note: '115 × 115 mm bed · flat items',
  },
  { label: 'Setup', value: '~30 minutes', note: 'One 120V outlet per station' },
  { label: 'Staffing', value: '2 people', note: 'Engraver + assistant' },
]

const guestSteps = [
  {
    title: 'Scan the booth QR',
    body: 'Guests scan the live code on the booth screen. It changes every few minutes, so a screenshot shared outside the venue stops working.',
  },
  {
    title: 'Type a name, pick a style',
    body: 'Guests type a name and choose from the fonts and icons you approved. Characters a font cannot show, names that will not fit, and obvious profanity are caught before submitting.',
  },
  {
    title: 'Check the live preview',
    body: 'The preview updates as they type, showing the layout on the item. Guests see what will be engraved before they submit.',
  },
  {
    title: 'Get a claim number',
    body: 'Each guest gets a claim number and a status page showing their place in the queue and an estimated wait, so they can keep exploring the event.',
  },
  {
    title: 'Engraved on site',
    body: 'Our engraver runs pieces from the queue on the enclosed laser while the assistant handles pickup. Typos can be corrected at the station before a piece is engraved.',
  },
  {
    title: 'Collect when ready',
    body: 'The status page flips to ready. A ready board at the booth lists finished claim numbers for anyone who closed the tab.',
  },
]

const items = [
  {
    label: '01 / SMALL & FLAT',
    title: 'Tags, cards, and keychains.',
    body: `Luggage tags, dog tags, leather cardholders, keychains, metal cards, and coasters suit a live station. The engraving area is up to ${facts.engravingArea} (a ${facts.bed} bed).`,
  },
  {
    label: '02 / YOUR ITEMS',
    title: 'Supply the items, plus spares.',
    body: `Clients usually supply the items, with spares for testing. We can also supply leather luggage tags, keychains, cardholders or stainless flasks, ${eventPricing.items.charAt(0).toLowerCase()}${eventPricing.items.slice(1)}. Before the event, we test on your actual item and confirm the look and time per piece. We don’t engrave guests’ own belongings.`,
  },
  {
    label: '03 / BOTTLES',
    title: 'Bottles, tested first.',
    body: 'Most perfume, wine and liquor bottles can be engraved live. It depends on the bottle, so we test your exact bottle beforehand. Glass engraves frosted, or we apply a marking compound (CerMark) for a dark mark. Very large pieces are engraved ahead of time at our New York studio.',
  },
]

const capacityRows = [
  { label: 'Name on a small, flat item', value: 'Roughly 40–50 per hour' },
  { label: 'Logo or icon added', value: 'Adds time per piece' },
  { label: 'Batch loading', value: 'About 250 in 3 hours' },
  { label: 'Second station', value: 'Shares one guest queue' },
  { label: 'Final rate', value: 'Confirmed after testing your item' },
]

const venueNeeds = [
  {
    title: 'A table-sized spot per station',
    body: 'We can bring the table and chair.',
  },
  {
    title: 'A standard 120V outlet per station',
    body: 'One regular wall outlet or power drop for each laser.',
  },
  {
    title: 'About 30 minutes to set up',
    body: 'Before guests arrive, inside your load-in window.',
  },
  {
    title: 'Internet for the guest app',
    body: 'We can bring our own hotspot if venue Wi-Fi is unreliable.',
  },
  {
    title: 'A certificate of insurance',
    body: 'We carry general liability insurance and can provide a COI naming your agency or venue.',
  },
]

const bookingSteps = [
  {
    title: 'Send the brief',
    body: 'Event date and hours, venue, expected guest count, the item and quantity, and the fonts, icons, or logo guests can choose from.',
  },
  {
    title: 'Test on your item',
    body: 'We engrave your actual item, ideally on spares, to confirm the settings, the look, and the time per piece. You approve the sample.',
  },
  {
    title: 'Reserve the date',
    body: `A ${facts.deposit} deposit reserves the date once you approve the sample. The balance is due before the event.`,
  },
]

const priceFactors = eventPricing.rows

const priceAnswer = eventPriceAnswer

const faqs = [
  {
    question: 'What is on-site laser engraving?',
    answer:
      'On-site, or live, laser engraving means the engraving happens at your event. PrintAWorld sets up a portable, enclosed laser station staffed by an engraver and an assistant. Guests personalize their piece from their phone and collect it when their claim number is ready.',
  },
  {
    question: 'How many pieces can a live engraving station do per hour?',
    answer: `${eventCapacity.summary} Adding a logo or icon takes longer. We confirm the rate after testing your actual item. A second station can share the same guest queue.`,
  },
  {
    question: 'What items work best for live event engraving?',
    answer: `Small, flat items with an engraving area up to ${facts.engravingArea}: luggage tags, dog tags, leather cardholders, keychains, metal cards, and coasters. Most bottles can be engraved live too; we test your exact bottle beforehand.`,
  },
  {
    question: 'What does the venue need to provide?',
    answer:
      'A table-sized spot and a standard 120V outlet for each station, plus about 30 minutes to set up. We can bring the table, chair, and our own internet hotspot.',
  },
  {
    question: 'Do guests need to download an app or create an account?',
    answer:
      'No. Guests scan the booth QR code and use a web page in their phone’s browser. There is no login, and the guest app does not collect email addresses.',
  },
  {
    question: 'How far ahead should we book on-site engraving in NYC?',
    answer: `Four or more weeks ahead is comfortable. We start with your brief, test on your item, and reserve the date with a ${facts.deposit} deposit after you approve the sample. The balance is due before the event.`,
  },
  {
    question: 'How much does on-site laser engraving cost?',
    answer: priceAnswer,
  },
  {
    question: 'Is live laser engraving the same as hand engraving?',
    answer:
      'No. A hand engraver or calligrapher letters each piece by hand. Our station uses an enclosed laser with the fonts and icons you approve, so every piece follows the same brand layout. For hand lettering on bottles or glassware, a hand engraver is the better fit.',
  },
  eventInsurance,
]

const quoteChecklist = [
  'Event date, hours, and venue address',
  'Expected guest count',
  'The item, quantity, and spares for testing, or ask us to source leather',
  'Fonts, icons, or logo artwork guests can choose from',
  'Venue or agency requirements, such as a COI',
]

module.exports = {
  onsiteEngravingFacts: facts,
  investment,
  page,
  images,
  keyFacts,
  guestSteps,
  items,
  capacityRows,
  venueNeeds,
  bookingSteps,
  priceFactors,
  faqs,
  quoteChecklist,
}
